import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Map,
  Users,
  Plus,
  Eye,
  ArrowRight,
  FileCheck,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Items
const sidebarItems = [
  {
    section: "Main",
    items: [
      { name: "Dashboard", path: "/landowner", icon: LayoutDashboard },
      { name: "My Lands", path: "/landowner/lands", icon: Map },
      { name: "My Consortiums", path: "/landowner/consortiums", icon: Users },
    ],
  },
];

// Dashboard Overview Component
const DashboardOverview = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/landowner`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      // Set empty dashboard state on error
      setDashboard({
        lands: [],
        consortiums: [],
        consents: [],
        summary: {
          total_lands: 0,
          total_area_sqm: 0,
          total_estimated_value: 0,
          active_consortiums: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
      </div>
    );
  }

  const stats = [
    {
      title: "Total Lands",
      value: dashboard?.summary?.total_lands || 0,
      icon: Map,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Total Area",
      value: `${(dashboard?.summary?.total_area_sqm || 0).toLocaleString()} sqm`,
      icon: TrendingUp,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Estimated Value",
      value: `₹${((dashboard?.summary?.total_estimated_value || 0) / 100000).toFixed(1)}L`,
      icon: FileCheck,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Active Consortiums",
      value: dashboard?.summary?.active_consortiums || 0,
      icon: Users,
      color: "bg-blue-100 text-[#4E80FF]",
    },
  ];

  return (
    <div className="space-y-6" data-testid="landowner-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Dashboard
          </h1>
          <p className="text-slate-600">Welcome back! Here's your land pooling overview.</p>
        </div>
        <Button
          className="btn-primary"
          onClick={() => navigate("/landowner/lands")}
          data-testid="add-land-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Land
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                  <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Lands */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Recent Lands</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/landowner/lands")}>
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {dashboard?.lands?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.lands.slice(0, 5).map((land) => (
                <div
                  key={land.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Map className="w-5 h-5 text-[#4E80FF]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{land.survey_number}</p>
                      <p className="text-sm text-slate-500">{land.location}</p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      land.status === "verified"
                        ? "default"
                        : land.status === "pending"
                        ? "secondary"
                        : "outline"
                    }
                    className={
                      land.status === "verified"
                        ? "bg-[#4E80FF]"
                        : land.status === "pending"
                        ? "bg-yellow-500"
                        : ""
                    }
                  >
                    {land.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Map className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No lands registered yet</p>
              <Button
                className="mt-4 btn-primary"
                onClick={() => navigate("/landowner/lands")}
              >
                Add Your First Land
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consortiums */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">My Consortiums</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/landowner/consortiums")}>
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {dashboard?.consortiums?.length > 0 ? (
            <div className="grid gap-4">
              {dashboard.consortiums.map((consortium) => (
                <div
                  key={consortium.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/consortium/${consortium.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#4E80FF]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{consortium.name}</p>
                      <p className="text-sm text-slate-500">{consortium.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">
                      {consortium.members?.length || 0} members
                    </p>
                    <Badge variant="outline" className="capitalize">
                      {consortium.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>You're not part of any consortium yet</p>
              <Button
                className="mt-4 btn-secondary"
                onClick={() => navigate("/landowner/consortiums")}
              >
                Explore Consortiums
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// My Lands Component
const MyLands = () => {
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    survey_number: "",
    area_sqm: "",
    location: "",
    district: "",
    state: "",
    pincode: "",
    land_type: "agricultural",
    ownership_type: "sole",
  });

  useEffect(() => {
    fetchLands();
  }, []);

  const fetchLands = async () => {
    try {
      const response = await axios.get(`${API}/lands`);
      setLands(response.data);
    } catch (error) {
      console.error("Failed to fetch lands:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/lands`, {
        ...formData,
        area_sqm: parseFloat(formData.area_sqm),
      });
      toast.success("Land added successfully!");
      setDialogOpen(false);
      setFormData({
        survey_number: "",
        area_sqm: "",
        location: "",
        district: "",
        state: "",
        pincode: "",
        land_type: "agricultural",
        ownership_type: "sole",
      });
      fetchLands();
    } catch (error) {
      toast.error("Failed to add land");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "verified":
        return <CheckCircle className="w-4 h-4 text-[#4E80FF]" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="my-lands-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">My Lands</h1>
          <p className="text-slate-600">Manage your land parcels for pooling</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-land-dialog-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Land
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add New Land</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Survey Number</Label>
                  <Input
                    value={formData.survey_number}
                    onChange={(e) =>
                      setFormData({ ...formData, survey_number: e.target.value })
                    }
                    placeholder="e.g., KH-123/456"
                    required
                    data-testid="survey-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Area (sqm)</Label>
                  <Input
                    type="number"
                    value={formData.area_sqm}
                    onChange={(e) =>
                      setFormData({ ...formData, area_sqm: e.target.value })
                    }
                    placeholder="e.g., 5000"
                    required
                    data-testid="area-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location/Village</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    placeholder="Village name"
                    required
                    data-testid="location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>District</Label>
                  <Input
                    value={formData.district}
                    onChange={(e) =>
                      setFormData({ ...formData, district: e.target.value })
                    }
                    placeholder="District"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({ ...formData, state: e.target.value })
                    }
                    placeholder="State"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) =>
                      setFormData({ ...formData, pincode: e.target.value })
                    }
                    placeholder="Pincode"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Land Type</Label>
                  <Select
                    value={formData.land_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, land_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agricultural">Agricultural</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ownership Type</Label>
                  <Select
                    value={formData.ownership_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, ownership_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole">Sole Owner</SelectItem>
                      <SelectItem value="joint">Joint Ownership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="submit-land-btn">
                  Add Land
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
      ) : lands.length > 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Survey No.</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Area (sqm)</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Est. Value</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lands.map((land) => (
                  <TableRow key={land.id}>
                    <TableCell className="font-mono font-medium">
                      {land.survey_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{land.location}</p>
                        <p className="text-sm text-slate-500">{land.district}</p>
                      </div>
                    </TableCell>
                    <TableCell>{land.area_sqm.toLocaleString()}</TableCell>
                    <TableCell className="capitalize">{land.land_type}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(land.status)}
                        <span className="capitalize">{land.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {land.estimated_value
                        ? `₹${(land.estimated_value / 100000).toFixed(1)}L`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Map className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
              No Lands Added Yet
            </h3>
            <p className="text-slate-500 mb-6">
              Start by adding your land parcels to check eligibility for pooling.
            </p>
            <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Land
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// My Consortiums Component
const MyConsortiums = () => {
  const navigate = useNavigate();
  const [consortiums, setConsortiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    location: "",
    target_area_sqm: "",
  });

  useEffect(() => {
    fetchConsortiums();
  }, []);

  const fetchConsortiums = async () => {
    try {
      const response = await axios.get(`${API}/consortiums`);
      setConsortiums(response.data);
    } catch (error) {
      console.error("Failed to fetch consortiums:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsortium = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/consortiums`, {
        ...formData,
        target_area_sqm: formData.target_area_sqm
          ? parseFloat(formData.target_area_sqm)
          : null,
      });
      toast.success("Consortium created successfully!");
      setDialogOpen(false);
      setFormData({ name: "", description: "", location: "", target_area_sqm: "" });
      navigate(`/consortium/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create consortium");
    }
  };

  return (
    <div className="space-y-6" data-testid="my-consortiums-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            My Consortiums
          </h1>
          <p className="text-slate-600">Manage your consortium memberships</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-secondary" data-testid="create-consortium-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Consortium
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Create New Consortium
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateConsortium} className="space-y-4">
              <div className="space-y-2">
                <Label>Consortium Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Green Valley Land Pool"
                  required
                  data-testid="consortium-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="e.g., Narela, Delhi"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Target Area (sqm) - Optional</Label>
                <Input
                  type="number"
                  value={formData.target_area_sqm}
                  onChange={(e) =>
                    setFormData({ ...formData, target_area_sqm: e.target.value })
                  }
                  placeholder="e.g., 100000"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of the consortium"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-secondary" data-testid="submit-consortium-btn">
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
      ) : consortiums.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {consortiums.map((consortium) => (
            <Card
              key={consortium.id}
              className="card-hover cursor-pointer"
              onClick={() => navigate(`/consortium/${consortium.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#4E80FF]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900">
                        {consortium.name}
                      </h3>
                      <p className="text-sm text-slate-500">{consortium.location}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {consortium.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-slate-500">Members</p>
                    <p className="font-semibold text-slate-900">
                      {consortium.members?.length || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Area</p>
                    <p className="font-semibold text-slate-900">
                      {consortium.total_area_sqm?.toLocaleString() || 0} sqm
                    </p>
                  </div>
                </div>
                <Button
                  className="w-full mt-4"
                  variant="outline"
                  data-testid={`view-consortium-${consortium.id}`}
                >
                  View Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
              No Consortiums Yet
            </h3>
            <p className="text-slate-500 mb-6">
              Create a new consortium or join an existing one to start pooling.
            </p>
            <div className="flex justify-center gap-4">
              <Button className="btn-secondary" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Consortium
              </Button>
              <Button variant="outline" onClick={() => navigate("/marketplace")}>
                Browse Marketplace
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Landowner Dashboard Component
const LandownerDashboard = () => {
  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <Routes>
        <Route index element={<DashboardOverview />} />
        <Route path="lands" element={<MyLands />} />
        <Route path="consortiums" element={<MyConsortiums />} />
      </Routes>
    </DashboardLayout>
  );
};

export default LandownerDashboard;
